/**
 * Adyen Helpers
 *
 * Utility functions for Adyen payment processing.
 * Separated per architecture spec: lib/checkout/payments/adyen/helpers.ts
 */

import type { PaymentAction as AdyenPaymentAction, ResultCode } from '@adyen/adyen-web';

import type { GenericErrorHandler } from '@/lib/checkout/errors/types';

import { PAYMENT_TYPES, type PaymentType } from '../types';
import type {
  ExtendedPaymentAction,
  TransactionResponseHandlerOptions,
  TransactionWithAction,
  TransactionWithEvents,
} from './types';

/**
 * Payment query parameters for redirects
 */
export const PAYMENT_QUERY_PARAMS = {
  additionalAction: 'additional-action-status',
  orderId: 'order-id',
  checkoutId: 'checkout-id',
  errorCode: 'error-code',
  failureSplitPaymentMaxPayments: 'failure-split-payment-max-payments',
  failureSplitPaymentMinAmount: 'failure-next-payment-min-amount',
  failureSplitPaymentMaxAmount: 'failure-next-payment-max-amount',
  successSplitPaymentMaxPayments: 'success-split-payment-max-payments',
  successSplitPaymentMinAmount: 'success-next-payment-min-amount',
  successSplitPaymentMaxAmount: 'success-next-payment-max-amount',
  payPerLinkToken: 'pay-per-link-token',
  transactionId: 'transaction-id',
} as const;

/**
 * Build the Adyen return URL for a payment attempt.
 *
 * Results from an earlier attempt stay in the address bar on pages that render
 * the outcome in place (pay-per-link), and the payment app appends its own
 * result on return. Reading the first value would then report the previous
 * attempt, so every param the payment app owns is cleared first.
 *
 * Example Input:
 * 'https://x.com/us-en/store/order/payment/?token=t1&additional-action-status=Refused', 'order-1'
 *
 * Example Output:
 * 'https://x.com/us-en/store/order/payment/?token=t1&order-id=order-1'
 */
export function buildPaymentReturnUrl(href: string, orderId: string): string {
  const url = new URL(href);

  for (const param of Object.values(PAYMENT_QUERY_PARAMS)) {
    url.searchParams.delete(param);
  }
  url.searchParams.set(PAYMENT_QUERY_PARAMS.orderId, orderId);

  return url.toString();
}

/**
 * Payment methods that support tokenization
 */
const TOKENIZABLE_PAYMENT_METHODS: Readonly<PaymentType[]> = [
  'paywithgoogle',
  'googlepay',
  'applepay',
  'scheme',
  'bcmc',
  'card',
];

/**
 * Non-Adyen payment methods (financing, bank transfer)
 */
const NON_ADYEN_PAYMENT_METHODS: Readonly<PaymentType[]> = [
  'v12',
  'cembra_installment',
  'cembra_invoice',
  'creditplus',
  'agos',
  PAYMENT_TYPES.OFFLINE,
  'orico_financing',
  'smbc',
];

/**
 * Payment methods that don't support split payment
 */
const NOT_SUPPORTED_SPLIT_PAYMENT_METHODS: Readonly<PaymentType[]> = [
  'v12',
  'creditplus',
  'agos',
  PAYMENT_TYPES.OFFLINE,
  PAYMENT_TYPES.ORICO_FINANCING,
  PAYMENT_TYPES.SMBC,
];

/**
 * Zero-decimal currencies where 1 major unit = 1 minor unit.
 * Adyen expects the amount as-is (e.g. ¥10000 → value: 10000).
 * Only includes currencies configured in this project's markets.
 */
const ZERO_DECIMAL_CURRENCIES: ReadonlySet<string> = new Set(['ISK', 'JPY', 'KRW', 'VND']);

/**
 * Three-decimal currencies where 1 major unit = 1000 minor units.
 * Adyen expects amount × 1000 (e.g. 10.500 KWD → value: 10500).
 * Only includes currencies configured in this project's markets.
 */
const THREE_DECIMAL_CURRENCIES: ReadonlySet<string> = new Set(['BHD', 'JOD', 'KWD', 'OMR']);

/**
 * Convert a major-unit amount to Adyen minor units based on currency.
 *
 * - Standard currencies (USD, EUR, GBP, CHF…): × 100
 * - Zero-decimal currencies (JPY, KRW, VND, ISK): × 1
 * - Three-decimal currencies (KWD, BHD, OMR, JOD): × 1000
 */
export const toMinorUnits = (amount: number, currency: string): number => {
  const code = currency.toUpperCase();
  if (ZERO_DECIMAL_CURRENCIES.has(code)) return Math.round(amount);
  if (THREE_DECIMAL_CURRENCIES.has(code)) return Math.round(amount * 1000);
  return Math.round(amount * 100);
};

/**
 * @deprecated Use `toMinorUnits(amount, currency)` — this assumes × 100 for all currencies.
 */
export const toMinorPrice = (amount: number): number => Math.round(amount * 100);

/** Adyen / gateway method id as returned on Drop-in state (may be unset). */
export type PaymentMethodTypeInput = PaymentType | string | null | undefined;

/**
 * Check if payment method is tokenizable (card, Apple Pay, Google Pay)
 */
export const isTokenizablePaymentMethod = (type: PaymentMethodTypeInput): boolean =>
  !!type && TOKENIZABLE_PAYMENT_METHODS.includes(type);

/**
 * Check if payment method is a wallet without a pay button (requires separate submit)
 */
export const isTokenizablePaymentMethodWithoutPayButton = (type: PaymentMethodTypeInput): boolean =>
  !!type &&
  (
    [
      PAYMENT_TYPES.APPLE_PAY,
      PAYMENT_TYPES.GOOGLE_PAY,
      PAYMENT_TYPES.PAY_WITH_GOOGLE,
    ] as PaymentType[]
  ).includes(type);

/**
 * Check if payment method is non-Adyen (financing, bank transfer)
 */
export const isNonAdyenPaymentMethod = (type: PaymentMethodTypeInput): boolean =>
  !!type && NON_ADYEN_PAYMENT_METHODS.includes(type);

/**
 * Check if payment method doesn't support split payment
 */
export const isNotSupportSplitPayment = (type: PaymentType | undefined | null): boolean =>
  !!type && NOT_SUPPORTED_SPLIT_PAYMENT_METHODS.includes(type);

/**
 * Handle GET redirect action
 */
const handleGetAction = (actionData: AdyenPaymentAction): void => {
  if (actionData.type === 'redirect' && actionData.url) {
    window.location.assign(actionData.url);
  }
};

/**
 * Submit a POST form programmatically
 */
const postFormForPostAction = async (
  url: string,
  actionData: Record<string, string>
): Promise<void> => {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = url;

  for (const [key, value] of Object.entries(actionData)) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = key;
    input.value = value;
    form.appendChild(input);
  }

  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

/**
 * Handle POST redirect action
 */
const handlePostAction = async (
  actionData: ExtendedPaymentAction,
  errorHandler: GenericErrorHandler
): Promise<void> => {
  if (!actionData.data || !actionData.url) {
    throw new Error('Unhandled post action - missing data or url');
  }

  try {
    await postFormForPostAction(actionData.url, actionData.data);
  } catch {
    errorHandler([{ code: 'Error' }], { errorType: 'adyen' });
  }
};

/**
 * Handle refused payment message
 */
export const handleRefusedMessage = (
  errorHandler: GenericErrorHandler,
  paymentType?: PaymentType,
  refusalReasonCode?: string
): void => {
  if (paymentType === 'cembra_invoice' || paymentType === 'cembra_installment') {
    errorHandler([{ code: 'Refused' }], { errorType: 'cembra' });
  } else if (paymentType === PAYMENT_TYPES.CREDIT_PLUS) {
    errorHandler([{ code: 'Refused' }], { errorType: 'creditPlus' });
  } else {
    errorHandler([{ code: 'Refused', message: refusalReasonCode }], {
      errorType: 'adyen',
    });
  }
};

/**
 * Result code for a transaction payload. A payload without one is treated as
 * authorised unless a balance is still due — callers that act on the authorised
 * outcome must apply the same rule.
 *
 * Example Input:
 * { action: { type: 'threeDS2' } }, undefined
 *
 * Example Output:
 * 'Authorised'
 */
export const resolveTransactionResultCode = (
  transactionData: Record<string, unknown> | null | undefined,
  amountRemainingToBePaid?: boolean
): ResultCode =>
  (transactionData?.resultCode as ResultCode) ??
  ((!amountRemainingToBePaid ? 'Authorised' : 'Unknown') as ResultCode);

/**
 * Handle transaction response from Adyen/Saleor
 */

export const handleTransactionResponse = async <T extends Record<string, unknown> | null>(
  transactionData: T,
  options: TransactionResponseHandlerOptions
  // oxlint-disable-next-line typescript/no-explicit-any
): Promise<any> => {
  const {
    amountRemainingToBePaid,
    errorHandler,
    handleAction,
    isReload,
    onAuthorize,
    paymentType,
    push,
    redirectPaths,
    reload,
    setIsTransactionSuccessfully,
  } = options;

  const resultCode = resolveTransactionResultCode(transactionData, amountRemainingToBePaid);

  const redirect = async (code: string): Promise<boolean | null> => {
    if (!push) {
      return null;
    }

    if (isReload && reload) {
      reload();
      return true;
    }

    const path = redirectPaths?.[code as keyof typeof redirectPaths];
    const defaultPath = redirectPaths?.default;

    if (path) {
      return await push(path);
    }

    if (defaultPath) {
      return await push(defaultPath);
    }

    return null;
  };

  // Handle PayPal pending with action
  if (
    resultCode === 'Pending' &&
    (paymentType === PAYMENT_TYPES.PAYPAL || paymentType === PAYMENT_TYPES.PAYPAL_EXPRESS) &&
    handleAction &&
    transactionData?.action
  ) {
    return handleAction(transactionData.action as AdyenPaymentAction);
  }

  switch (resultCode) {
    case 'Authorised':
      onAuthorize?.(transactionData);
      return await redirect('Authorised');

    case 'ChallengeShopper':
    case 'IdentifyShopper':
    case 'RedirectShopper': {
      const actionData = transactionData?.action as ExtendedPaymentAction | undefined;

      if (!actionData) {
        throw new Error('Missing `action` data in transaction. Cannot handle additional action.');
      }

      if (handleAction) {
        return handleAction(actionData);
      }

      if (actionData.method === 'GET') {
        handleGetAction(actionData);
        return;
      } else if (actionData.method?.toUpperCase() === 'POST') {
        return await handlePostAction(actionData, errorHandler);
      }

      throw new Error(`Unhandled '${resultCode}' action.`);
    }

    case 'Refused':
      setIsTransactionSuccessfully?.(false);
      handleRefusedMessage(
        errorHandler,
        paymentType,
        transactionData?.refusalReasonCode as string | undefined
      );
      return false;

    default:
      errorHandler([{ code: 'Error' }], { errorType: 'adyen' });
      return await redirect('default');
  }
};

/**
 * Check if redirect indicates authorization success
 */
export const isAuthorisedRedirect = (searchParams: URLSearchParams): boolean => {
  const resultCode = searchParams.get(PAYMENT_QUERY_PARAMS.additionalAction) as ResultCode | null;
  return resultCode === 'Authorised';
};

/**
 * Check if transaction events contain authorization success
 */
export const isAuthorizedTransactions = (transaction: TransactionWithEvents | null): boolean => {
  const events = transaction?.events ?? [];
  return events.some((event) => event?.type === 'AUTHORIZATION_SUCCESS');
};

/**
 * Check if any transaction event was successful
 */
export const isSuccessfulTransaction = (transaction: TransactionWithEvents | null): boolean => {
  const events = transaction?.events ?? [];
  return events.some((event) => event?.type?.endsWith('SUCCESS'));
};

/**
 * Check if transaction failed
 */
export const isFailedTransaction = (transaction: TransactionWithEvents | null): boolean => {
  const lastEvent = transaction?.events?.[0];
  return !!lastEvent?.type?.endsWith('FAILURE');
};

/**
 * Check if transaction requires additional action (3DS, redirect)
 */
export const isTransactionWithAdditionalAction = (
  transaction: TransactionWithAction | null
): boolean => !!transaction?.data?.action;

/**
 * Calculate deposit amount for pre-orders
 */
export const calculateDepositAmount = (total: number, depositPercentage: number): number =>
  Number(((total * depositPercentage) / 100).toFixed(2));

/**
 * Extract split payment next payment config from query params
 */
export const extractSplitPaymentNextPayment = (
  searchParams: URLSearchParams
): { maxPayments: number; maxAmount: number; minAmount: number } | undefined => {
  const resultCode = searchParams.get(PAYMENT_QUERY_PARAMS.additionalAction) as ResultCode | null;

  if (resultCode === 'Authorised') {
    return {
      maxPayments: Number(
        searchParams.get(PAYMENT_QUERY_PARAMS.successSplitPaymentMaxPayments) || 0
      ),
      maxAmount: Number(searchParams.get(PAYMENT_QUERY_PARAMS.successSplitPaymentMaxAmount) || 0),
      minAmount: Number(searchParams.get(PAYMENT_QUERY_PARAMS.successSplitPaymentMinAmount) || 0),
    };
  } else if (resultCode === 'Error' || resultCode === 'Refused') {
    return {
      maxPayments: Number(
        searchParams.get(PAYMENT_QUERY_PARAMS.failureSplitPaymentMaxPayments) || 0
      ),
      maxAmount: Number(searchParams.get(PAYMENT_QUERY_PARAMS.failureSplitPaymentMaxAmount) || 0),
      minAmount: Number(searchParams.get(PAYMENT_QUERY_PARAMS.failureSplitPaymentMinAmount) || 0),
    };
  }
};

/**
 * Rebuild the instalment slice of a transaction payload from a redirect return.
 *
 * A full-page return (3DS, PayPal) drops the in-memory transaction payload, but
 * the payment app repeats its instalment details on the return URL. Restoring
 * them keeps `getNextSplitPaymentToken` usable as the fallback for when the
 * balance check itself is unreachable.
 *
 * Example Input:
 * new URLSearchParams('additional-action-status=Authorised&pay-per-link-token=t1&success-split-payment-max-payments=3')
 *
 * Example Output:
 * { breitling: { payPerLinkToken: 't1', splitPaymentMaxPayments: 3 } }
 */
export const buildRedirectTransactionData = (
  searchParams: URLSearchParams
): Record<string, unknown> => {
  const payPerLinkToken = searchParams.get(PAYMENT_QUERY_PARAMS.payPerLinkToken);
  const splitPaymentMaxPayments = extractSplitPaymentNextPayment(searchParams)?.maxPayments ?? 0;

  if (!payPerLinkToken || splitPaymentMaxPayments <= 0) return {};

  return { breitling: { payPerLinkToken, splitPaymentMaxPayments } };
};

/**
 * Check if there's a next split payment
 */
export const getSplitPaymentHasNextPayment = (searchParams: URLSearchParams): boolean => {
  const splitPaymentNextPayment = extractSplitPaymentNextPayment(searchParams);
  return !!(splitPaymentNextPayment?.maxPayments && splitPaymentNextPayment.maxPayments > 0);
};

/**
 * Get next split payment token from transaction data
 */
export const getNextSplitPaymentToken = <T extends Record<string, unknown> | null>(
  transactionData: T
): string | undefined => {
  // oxlint-disable-next-line typescript/no-explicit-any
  const breitlingData = (transactionData as any)?.breitling;
  const maxPayments = Number(breitlingData?.splitPaymentMaxPayments);
  if (maxPayments > 0) {
    return breitlingData?.payPerLinkToken;
  }
};

/**
 * Check if Oney payment type
 */
const oneyPaymentTypePattern = /^facilypay_(3|4|6|10|12)x$/;

export const checkIsOneyPaymentByType = (type?: PaymentType | null): boolean =>
  !!type && oneyPaymentTypePattern.test(type);

/**
 * Get installment count from Oney payment type
 */
export const getInstallmentCount = (type: PaymentType | null | undefined): number => {
  const match = type?.match(oneyPaymentTypePattern);
  return match ? Number(match[1]) : 1;
};

/**
 * Calculate per-installment amount
 */
export const getInstallmentAmount = (total: number, type: PaymentType | null | undefined): number =>
  total / getInstallmentCount(type);

/**
 * Check if checkout should redirect to delivery (missing required fields)
 */
export const checkShouldRedirectToDelivery = (
  shippingAddress: unknown,
  billingAddress: unknown,
  deliveryMode?: string,
  boutiqueExtRef?: string,
  isShippingRequired?: boolean
): boolean => {
  return (
    (!shippingAddress && isShippingRequired) ||
    !billingAddress ||
    (!deliveryMode && isShippingRequired) ||
    (deliveryMode === 'BOUTIQUE_PICKUP' && !boutiqueExtRef)
  );
};
